import MachineActionManagementColumnModel from './machine-action-management-column-model'
import React, { useState, useEffect } from 'react'
import CommonTablePage from '../../../../components/common/common-table/common-table-page'
import { urlKey } from '../../../../services/api/api-urls'
import { Select, Button, Collapse, Modal, Space, Tag, Alert, Typography } from 'antd'
import { ApiRequest } from '../../../../services/api/api'
import SweetAlertService from '../../../../services/lib/utils/sweet-alert-service'
import { PlusOutlined, EditOutlined, DeleteOutlined, StarOutlined, StarFilled, ExclamationCircleOutlined } from '@ant-design/icons'

const { Panel } = Collapse
const { Text } = Typography

const MachineActionManagementPage = (props: {baseProductId: any, productTypeId?: any, onDialogClose?: () => void, isEmbedded?: boolean}): any => {
  console.log('MachineActionManagementPage props:', props)
  console.log('baseProductId:', props.baseProductId)

  // 处理错误的辅助函数
  const handleError = async (error: any, defaultMessage: string) => {
    console.error('Error occurred:', error)

    // 尝试提取详细错误信息
    let errorMessage = defaultMessage
    let detailedError = null

    if (error?.response?.data) {
      const errorData = error.response.data
      if (errorData.message?.message) {
        errorMessage = errorData.message.message
      }
      if (errorData.message?.innerMessage) {
        detailedError = errorData.message.innerMessage
      }
    } else if (error?.message) {
      errorMessage = error.message
    }

    // 显示主要错误消息
    await SweetAlertService.errorMessage(errorMessage)

    // 如果有详细错误信息，显示详情对话框
    if (detailedError) {
      setErrorDetails({
        message: errorMessage,
        details: detailedError,
        statusCode: error?.response?.status || error?.statusCode || 500
      })
      setErrorModalVisible(true)
    }
  }

  const [routings, setRoutings] = useState<any[]>([])
  const [selectedRoutingId, setSelectedRoutingId] = useState<number | null>(null)
  const [routingModalVisible, setRoutingModalVisible] = useState(false)
  const [editingRouting, setEditingRouting] = useState<any>(null)
  const [triggerReset, setTriggerReset] = useState(0)
  const [selectedProductTypeId, setSelectedProductTypeId] = useState<number | null>(null)
  const [actionRoutings, setActionRoutings] = useState<any[]>([])
  const [productTypes, setProductTypes] = useState<any[]>([])

  // 错误详情状态
  const [errorModalVisible, setErrorModalVisible] = useState(false)
  const [errorDetails, setErrorDetails] = useState<any>(null)

  // 获取所有 Product Types
  useEffect(() => {
    fetchProductTypes()
  }, [])

  const fetchProductTypes = async () => {
    try {
      const response = await ApiRequest({
        url: 'ProductType/GetAllProductType',
        method: 'get'
      })
      const types = response?.data?.data || []
      setProductTypes(types)
      console.log('Product types loaded:', types)
    } catch (error) {
      await handleError(error, 'Failed to load product types')
    }
  }

  // 获取所有 Routing
  useEffect(() => {
    fetchRoutings()
  }, [props.baseProductId])

  const fetchRoutings = async () => {
    try {
      const response = await ApiRequest({
        url: `BaseProductRouting/GetByBaseProductId?baseProductId=${props.baseProductId}`,
        method: 'get'
      })
      const routingList = response?.data?.data || []
      // 标准化数据结构，确保字段名一致
      const normalizedRoutings = routingList.map((routing: any) => ({
        ...routing,
        baseProductRoutingId: routing.id, // 统一字段名
        routingName: routing.routingName || routing.productTypeName || routing.baseProductName || 'Unnamed Routing'
      }))
      setRoutings(normalizedRoutings)
      console.log('Base Product Routings loaded:', normalizedRoutings)
    } catch (error) {
      await handleError(error, 'Failed to load routings')
    }
  }

  // 获取选中 Routing 的 Action Routings
  useEffect(() => {
    if (selectedRoutingId) {
      fetchActionRoutings()
    } else {
      setActionRoutings([])
    }
  }, [selectedRoutingId, triggerReset])

  const fetchActionRoutings = async () => {
    try {
      const response = await ApiRequest({
        url: `BaseProductActionRouting/GetByBaseProductRoutingId?baseProductRoutingId=${selectedRoutingId}`,
        method: 'get'
      })
      const actionRoutingList = response?.data?.data || []
      setActionRoutings(actionRoutingList)
      console.log('Action Routings loaded:', actionRoutingList)
    } catch (error) {
      await handleError(error, 'Failed to load action routings')
    }
  }

  // 添加 Routing
  const handleAddRouting = async () => {
    if (!selectedProductTypeId) {
      await SweetAlertService.errorMessage('Please select a product type')
      return
    }

    // 判断是否为第一条 routing
    const isFirstRouting = routings.length === 0

    try {
      await ApiRequest({
        url: 'BaseProductRouting/AddBaseProductRouting',
        method: 'post',
        data: {
          baseProductId: Number(props.baseProductId), // 转换为 number
          productTypeId: Number(selectedProductTypeId), // 转换为 number
          isDefault: isFirstRouting, // 第一条设为默认，后续的都是 false
          isActive: true,
          remark: 'Created from Machine Action Management'
        }
      })

      await SweetAlertService.successMessage('Routing added successfully!')
      setSelectedProductTypeId(null)
      setRoutingModalVisible(false)
      fetchRoutings()
    } catch (error) {
      await handleError(error, 'Failed to add routing. Please try again.')
    }
  }

  // 编辑 Routing
  const handleEditRouting = async () => {
    if (!editingRouting || !selectedProductTypeId) {
      await SweetAlertService.errorMessage('Please select a product type')
      return
    }

    try {
      await ApiRequest({
        url: 'BaseProductRouting/UpdateBaseProductRouting',
        method: 'put',
        data: {
          baseProductRoutingId: Number(editingRouting.baseProductRoutingId || editingRouting.id), // 转换为 number
          baseProductId: Number(props.baseProductId), // 转换为 number
          productTypeId: Number(selectedProductTypeId), // 转换为 number
          isDefault: editingRouting.isDefault || false, // 保持原有的默认状态
          isActive: editingRouting.isActive !== undefined ? editingRouting.isActive : true, // 保持原有状态
          remark: editingRouting.remark || '' // 保持原有备注
        }
      })

      await SweetAlertService.successMessage('Routing updated successfully!')
      setSelectedProductTypeId(null)
      setEditingRouting(null)
      setRoutingModalVisible(false)
      fetchRoutings()
    } catch (error) {
      await handleError(error, 'Failed to update routing. Please try again.')
    }
  }

  // 设置默认 Routing
  const handleSetDefaultRouting = async (routing: any) => {
    if (routing.isDefault) {
      await SweetAlertService.errorMessage('This routing is already set as default.')
      return
    }

    const routingName = routing.routingName || routing.productTypeName || 'this routing'
    const result = await SweetAlertService.confirmMessage(
      `Are you sure you want to set "${routingName}" as the default routing?` +
      `\n\nThis will remove the default status from the current default routing.`
    )

    if (result) {
      try {
        await ApiRequest({
          url: 'BaseProductRouting/SetDefaultRouting',
          method: 'post',
          data: {
            baseProductId: props.baseProductId,
            baseProductRoutingId: routing.baseProductRoutingId || routing.id
          }
        })

        await SweetAlertService.successMessage('Default routing updated successfully!')
        fetchRoutings()
      } catch (error) {
        await handleError(error, 'Failed to set default routing. Please try again.')
      }
    }
  }

  // 删除 Routing
  const handleDeleteRouting = async (routing: any) => {
    const routingName = routing.routingName || routing.productTypeName || 'this routing'
    let message = `Are you sure you want to delete routing "${routingName}"?`

    // 如果是默认 routing，添加额外警告
    if (routing.isDefault) {
      message += `\n\n⚠️ WARNING: This is the DEFAULT routing. Deleting it may affect system behavior.`
    }

    message += `\n\nThis will also delete all associated machine actions.`

    const result = await SweetAlertService.confirmMessage(message)

    if (result) {
      try {
        await ApiRequest({
          url: 'BaseProductRouting/DeleteBaseProductRouting',
          method: 'delete',
          params: { id: routing.baseProductRoutingId || routing.id }
        })

        await SweetAlertService.successMessage('Routing deleted successfully!')

        // 如果删除的是当前选中的 routing，清空选择
        if (selectedRoutingId === (routing.baseProductRoutingId || routing.id)) {
          setSelectedRoutingId(null)
        }

        fetchRoutings()
      } catch (error) {
        await handleError(error, 'Failed to delete routing. Please try again.')
      }
    }
  }

  // 打开添加 Modal
  const openAddModal = () => {
    setSelectedProductTypeId(null)
    setEditingRouting(null)
    setRoutingModalVisible(true)
  }

  // 打开编辑 Modal
  const openEditModal = (routing: any) => {
    setSelectedProductTypeId(routing.productTypeId || null)
    setEditingRouting(routing)
    setRoutingModalVisible(true)
  }

  // 关闭 Modal
  const closeRoutingModal = () => {
    setSelectedProductTypeId(null)
    setEditingRouting(null)
    setRoutingModalVisible(false)
  }

  return (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: props.isEmbedded ? 'flex-end' : 'space-between', alignItems: 'center' }}>
        {!props.isEmbedded && <h3 style={{ margin: 0 }}>Product Routings</h3>}
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openAddModal}
        >
          Add Routing
        </Button>
      </div>

      {/* Routing 列表 */}
      {routings.length > 0 ? (
        <Collapse
          activeKey={selectedRoutingId ? String(selectedRoutingId) : undefined}
          onChange={(keys) => {
            const activeKey = Array.isArray(keys) ? keys[0] : keys
            setSelectedRoutingId(activeKey ? Number(activeKey) : null)
          }}
          style={{ marginBottom: 16 }}
        >
          {routings.map((routing: any) => (
            <Panel
              header={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 500 }}>{routing.routingName}</span>
                    {routing.isDefault && (
                      <Tag
                        color="blue"
                        icon={<StarFilled />}
                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        Default
                      </Tag>
                    )}
                  </div>
                  <Space size="small">
                    {!routing.isDefault && (
                      <Button
                        size="small"
                        type="primary"
                        ghost
                        icon={<StarOutlined />}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleSetDefaultRouting(routing)
                        }}
                        style={{ lineHeight: '1', display: 'flex', alignItems: 'center' }}
                      >
                        Set Default
                      </Button>
                    )}
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteRouting(routing)
                      }}
                      style={{ lineHeight: '1', display: 'flex', alignItems: 'center' }}
                    >
                      Delete
                    </Button>
                  </Space>
                </div>
              }
              key={routing.baseProductRoutingId || routing.id}
            >
              {/* Machine Actions for this Routing */}
              <CommonTablePage
                key={`machine-action-${routing.baseProductRoutingId || routing.id}-${triggerReset}`}
                urlInfoKey={urlKey.BaseProductActionRouting}
                getAllUrl={`BaseProductActionRouting/GetByBaseProductRoutingId?baseProductRoutingId=${routing.baseProductRoutingId || routing.id}`}
                title="Machine Actions"
                mappingRenderData={async (data: any) => {
                  try {
                    // 获取所有机器数据
                    const machinesResponse = await ApiRequest({
                      url: 'Machine/GetAllMachine',
                      method: 'get'
                    })
                    const allMachines = machinesResponse?.data?.data || []

                    // 标准化数据结构
                    const processedData = data.map((row: any) => {
                      // 找到对应的机器对象
                      const machineObj = allMachines.find((m: any) => m.machineId === row.machineId)

                      // 构建 action 对象
                      const actionObj = {
                        actionId: row.actionId,
                        actionName: row.actionName
                      }

                      return {
                        ...row,
                        id: row.id, // 保留原始 ID
                        baseProductActionRoutingId: row.id, // 标准化字段名
                        action: actionObj, // action 对象
                        machine: machineObj || null, // machine 对象
                        sequence: row.sequenceNo || row.sequence || 0 // 标准化 sequence 字段
                      }
                    })
                    return processedData
                  } catch (error) {
                    console.error('Failed to load machines in mappingRenderData:', error)
                    return data
                  }
                }}
                mappingUpdateData={(dataDetail: any) => {
                  let processedData = { ...dataDetail }
                  if (dataDetail.machine !== undefined && dataDetail.machine !== null) {
                    processedData.machineId = dataDetail.machine
                  }
                  // 确保使用正确的字段名
                  return {
                    ...processedData,
                    baseProductRoutingId: routing.baseProductRoutingId || routing.id,
                    sequenceNo: processedData.sequence || processedData.sequenceNo
                  }
                }}
                column={MachineActionManagementColumnModel.Column}
                isNotAddable={false}
              />
            </Panel>
          ))}
        </Collapse>
      ) : (
        <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
          No routings configured yet. Click "Add Routing" to create one.
        </div>
      )}

      {/* Routing Modal */}
      <Modal
        title={editingRouting ? 'Edit Routing' : 'Add Routing'}
        open={routingModalVisible}
        onOk={editingRouting ? handleEditRouting : handleAddRouting}
        onCancel={closeRoutingModal}
        okText="Save"
        cancelText="Cancel"
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            Product Type:
          </label>
          <Select
            style={{ width: '100%' }}
            value={selectedProductTypeId}
            onChange={(value) => setSelectedProductTypeId(value)}
            placeholder="Select a product type"
            showSearch
            optionFilterProp="children"
          >
            {productTypes.map((type: any) => (
              <Select.Option key={type.productTypeId} value={type.productTypeId}>
                {type.productTypeName}
              </Select.Option>
            ))}
          </Select>
        </div>
      </Modal>

      {/* Error Details Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ExclamationCircleOutlined style={{ fontSize: '20px', color: '#ff4d4f' }} />
            <span>Error Details</span>
          </div>
        }
        open={errorModalVisible}
        onCancel={() => setErrorModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setErrorModalVisible(false)}>
            Close
          </Button>
        ]}
        width={700}
      >
        {errorDetails && (
          <div>
            <Alert
              message={errorDetails.message}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <div style={{ marginBottom: 8 }}>
              <strong>Status Code:</strong> {errorDetails.statusCode}
            </div>
            <div>
              <strong>Detailed Error:</strong>
              <pre style={{
                backgroundColor: '#f5f5f5',
                padding: '12px',
                borderRadius: '4px',
                fontSize: '12px',
                maxHeight: '300px',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all'
              }}>
                {errorDetails.details}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default MachineActionManagementPage
